import React, { useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { addDays, endOfWeek, startOfWeek, format } from 'date-fns'
import jstz from 'jstz'

import { Button } from '../../../common/components/Button'
import DatePicker from '../../../common/components/DatePicker'
import GroupedButton from '../../../common/components/GroupedButton'
import { Combobox } from '../../../common/components/combobox'
import {
    Report,
    useReportGetReportsForAdminQuery,
    useLazyReportGetReportDataQuery,
    useReportGetReportsForPartnerQuery,
} from '../../../common/redux/api/web-api'
import { TooltipIcon } from '../../../lib/icons/tooltip-icon'
import Text from '../../../ui/Text'
import { WidgetLayout } from '../../../lib/widget-layout'
import { SLabelText } from '../../../common/styles/Form'
import { notifySuccess, notifyError } from '../../../common/features/notificationsManager/Notify'
import { isAdminDomain, isPartnerDomain } from '../../../lib/currentDomain'
import { RootState as AdminRootState } from '../../../admin/redux/store'
import { RootState as PartnerRootState } from '../../../partner/redux/store'
import useMediaQueryHook from '../../../common/hooks/useMediaQueryHook'
import { ScreenDeviceType } from '../../../common/enums/screenDeviceTypeEnum'
import AlertModal from '../../../ui/AlertModal'
import useText from '../../hooks/useText'

import { generateReports, PeriodInterval, getDatesForInterval } from './ReportGeneratorWidget.utils'

type ReportsPageInputs = {
    reportId?: number
    fromDate: Date
    toDate: Date
}

const dateFormat = 'yyyy-MM-dd'

export const ReportsGeneratorWidget = () => {
    const text = useText()
    const { selectedCompanyId, partnerId } = useSelector((state: AdminRootState | PartnerRootState) => state.user)
    const screenSize = useMediaQueryHook()

    const [intervalSelected, setIntervalSelected] = useState<PeriodInterval | undefined>('CurrentWeek')
    const [isGeneratingReport, setIsGeneratingReport] = useState(false)
    const [showHelpModal, setShowHelpModal] = useState(false)

    const periodOptions = useMemo(
        () => [
            {
                label: text('btnCurrentWeek'),
                value: 'CurrentWeek',
            },
            {
                label: text('btnPreviousWeek'),
                value: 'PreviousWeek',
            },
            {
                label: text('btnCurrentMonth'),
                value: 'CurrentMonth',
            },
            {
                label: text('btnPreviousMonth'),
                value: 'PreviousMonth',
            },
            {
                label: text('btnCurrentYear'),
                value: 'CurrentYear',
            },
            {
                label: text('btnPreviousYear'),
                value: 'PreviousYear',
            },
        ],
        [text]
    )

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { isValid },
    } = useForm<ReportsPageInputs>({
        defaultValues: {
            reportId: undefined,
            fromDate: startOfWeek(new Date(), { weekStartsOn: 1 }),
            toDate: endOfWeek(new Date(), { weekStartsOn: 1 }),
        },
    })

    const { data: adminReports } = useReportGetReportsForAdminQuery(
        {
            selectedCompanyId,
        },
        {
            skip: !isAdminDomain,
        }
    )

    const { data: partnerReports } = useReportGetReportsForPartnerQuery(
        {
            partnerId,
        },
        {
            skip: !isPartnerDomain,
        }
    )

    const reportsOptions = useMemo(() => {
        let reports: Report[]

        if (isAdminDomain && adminReports) {
            reports = adminReports
        } else if (isPartnerDomain && partnerReports) {
            reports = partnerReports
        } else {
            return
        }

        return reports?.map((nextReport) => ({
            value: nextReport.id,
            label: nextReport.name,
        }))
    }, [adminReports, partnerReports])

    const [getReports] = useLazyReportGetReportDataQuery()
    const generateReportCompany = async ({ reportId, fromDate, toDate }: ReportsPageInputs) => {
        try {
            setIsGeneratingReport(true)
            const from = format(fromDate, dateFormat)
            const to = format(addDays(toDate, 1), dateFormat)

            notifySuccess(text('lblGeneratingReport'))

            const tz = jstz.determine()

            const reportData = await getReports({
                reportId,
                companyId: selectedCompanyId,
                fromInLocalTime: from,
                toInLocalTime: to,
                timeZone: tz.name(),
            }).unwrap()

            generateReports(reportData, from, to)
            notifySuccess(text('lblReportGenerateSuccess'))
        } catch (error) {
            notifyError(text('Failed to generate report'))
        } finally {
            setIsGeneratingReport(false)
        }
    }

    const generateReportPartner = async ({ reportId, fromDate, toDate }: ReportsPageInputs) => {
        try {
            setIsGeneratingReport(true)
            const from = format(fromDate, dateFormat)
            const to = format(addDays(toDate, 1), dateFormat)

            notifySuccess(text('lblGeneratingReport'))

            const tz = jstz.determine()

            const reportData = await getReports({
                reportId,
                partnerId,
                fromInLocalTime: from,
                toInLocalTime: to,
                timeZone: tz.name(),
            }).unwrap()

            generateReports(reportData, from, to)
            notifySuccess(text('lblReportGenerateSuccess'))
        } catch (error) {
            notifyError(text('Failed to generate report'))
        } finally {
            setIsGeneratingReport(false)
        }
    }
    const getReportDescription = () => {
        const reports = isAdminDomain ? adminReports : partnerReports

        return reports?.find((report) => report.id === reportId)?.description
    }

    const reportId = watch('reportId')
    const fromDate = watch('fromDate')
    const toDate = watch('toDate')

    const setDateBasedOnInterval = (interval: PeriodInterval) => {
        const dateInterval = getDatesForInterval(interval)

        if (!dateInterval) return

        if (dateInterval.fromDate) setValue('fromDate', dateInterval.fromDate)
        if (dateInterval.toDate) setValue('toDate', dateInterval.toDate)

        setIntervalSelected(interval)
    }

    return (
        <>
            <WidgetLayout
                title={text('lblReportGenerator')}
                headerButtons={
                    <Button icon={<TooltipIcon />} onClick={() => setShowHelpModal(true)} className="ghost" />
                }
                actionButtons={
                    <Button
                        onClick={
                            isAdminDomain ? handleSubmit(generateReportCompany) : handleSubmit(generateReportPartner)
                        }
                        size="sm"
                        disabled={!isValid}
                        loading={isGeneratingReport}
                        data-testid="generate-btn"
                    >
                        {text('btnGenerate')}
                    </Button>
                }
            >
                <div className="flex flex-wrap md:flex-nowrap gap-4 field">
                    <div className="field w-full w-12 md:w-6">
                        <Controller
                            name="reportId"
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { onChange, value } }) => (
                                <Combobox
                                    id="selecter-report"
                                    data-testid="select-report-dropdown"
                                    options={reportsOptions}
                                    value={value}
                                    onChange={(option) => onChange(option.value)}
                                    labelText={text('lblReport')}
                                />
                            )}
                        />
                    </div>
                    <div className="w-12 md:w-6 flex gap-2">
                        <Controller
                            name="fromDate"
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { onChange, value } }) => (
                                <DatePicker
                                    id="fromDate"
                                    data-testid="fromDate-input"
                                    wrapperClassName="w-6"
                                    labelText={text('lblFromField')}
                                    max={format(toDate, dateFormat)}
                                    value={format(value, dateFormat)}
                                    onChange={(event) => {
                                        onChange(event.target.valueAsDate)
                                        setIntervalSelected(undefined)
                                    }}
                                />
                            )}
                        />
                        <Controller
                            name="toDate"
                            control={control}
                            rules={{ required: true }}
                            render={({ field: { onChange, value } }) => (
                                <DatePicker
                                    id="toDate"
                                    data-testid="toDate-input"
                                    wrapperClassName="w-6"
                                    labelText={text('lblToField')}
                                    min={format(fromDate, dateFormat)}
                                    value={format(value, dateFormat)}
                                    onChange={(event) => {
                                        onChange(event.target.valueAsDate)
                                        setIntervalSelected(undefined)
                                    }}
                                />
                            )}
                        />
                    </div>
                </div>
                <div className="field">
                    <div className="flex justify-content-between mb-2">
                        <SLabelText className="label-indent" data-testid="earthingSystem-label">
                            {text('lblPreDefinedIntervals')}
                        </SLabelText>
                    </div>
                    {screenSize === ScreenDeviceType.Tablet ? (
                        <Combobox
                            enableDefaultOption={false}
                            id="interval"
                            data-testid="interval-dropdown"
                            options={periodOptions}
                            value={intervalSelected}
                            onChange={(option) => setDateBasedOnInterval(option.value)}
                        />
                    ) : (
                        <GroupedButton
                            options={periodOptions}
                            onChange={(value) => setDateBasedOnInterval(value)}
                            value={intervalSelected}
                        />
                    )}
                </div>
                {reportId && (
                    <div className="field mt-2 mb-4">
                        <div className="flex justify-content-between mb-2">
                            <SLabelText className="label-indent" data-testid="earthingSystem-label">
                                {text('lblSelectedReportDescription')}
                            </SLabelText>
                        </div>
                        <div>
                            <i>{getReportDescription()}</i>
                        </div>
                    </div>
                )}
            </WidgetLayout>
            {showHelpModal && (
                <AlertModal
                    title={text('lblHelpModal')}
                    message={<Text code="msgImportingReports" />}
                    close={() => setShowHelpModal(false)}
                    dismiss={() => setShowHelpModal(false)}
                    okButtonText={text('btnClose')}
                />
            )}
        </>
    )
}
